import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  LinearProgress,
  Typography,
  makeStyles
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import type { Theme } from 'src/theme';
import Page from 'src/components/Page';
import Logo from 'src/components/Logo';
import { v2UrlFor } from 'src/v2Sunset';
import type { V2SunsetPage } from 'src/v2Sunset';

// Long enough to register the headline, short enough not to feel like a wall. At 1s the
// body copy is not readable in passing — the card is a signpost, not a notice, and the
// same reassurance is repeated on the v2 login page it lands on.
const AUTO_REDIRECT_MS = 1000;

const COPY: Record<V2SunsetPage, { title: string; heading: string; body: string }> = {
  register: {
    title: 'Sign up | Tracktaco Tracking',
    heading: 'TrackTaco has a new home',
    // Whoever lands here has no account yet, so there is nothing to reassure them
    // about and no claim they can check. Say where they are going, nothing more.
    body: 'Same tracking numbers, same pricing.',
  },
  login: {
    title: 'Sign in | Tracktaco Tracking',
    heading: 'TrackTaco has moved',
    body: 'Sign in on the new TrackTaco with the same email and password. Your credits, history and API key are already there.',
  },
  resetPassword: {
    title: 'Reset password | Tracktaco Tracking',
    heading: 'TrackTaco has moved',
    body: 'Password resets now happen on the new TrackTaco.',
  },
  // Search is meant to run 'immediate' — someone arriving from the Chrome extension is
  // mid-task and the card would only be in the way. Copy exists so the switch has no hole.
  search: {
    title: 'Search | Tracktaco Tracking',
    heading: 'TrackTaco has moved',
    body: 'Your search is being carried over to the new TrackTaco.',
  },
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: '100vh'
  },
  cardContent: {
    padding: theme.spacing(5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  },
  // Same violet as the launch banner's CTA — one recognisable "this is the new app" colour.
  cta: {
    backgroundColor: '#7c4dff',
    color: '#fff',
    fontWeight: 700,
    boxShadow: '0 2px 10px rgba(124,77,255,0.5)',
    '&:hover': { backgroundColor: '#651fff' },
  },
  progress: {
    width: '100%',
    marginTop: theme.spacing(4)
  }
}));

interface RedirectToV2Props {
  page: V2SunsetPage;
}

/**
 * No UI at all — for 'immediate' pages reached by in-app navigation, which `index.tsx`
 * cannot catch because the browser never asks the server for the URL.
 */
export const RedirectNowToV2: FC<RedirectToV2Props> = ({ page }) => {
  useEffect(() => { window.location.replace(v2UrlFor(page)); }, [page]);
  return null;
};

const RedirectToV2: FC<RedirectToV2Props> = ({ page }) => {
  const classes = useStyles();
  const [url] = useState(() => v2UrlFor(page));
  const copy = COPY[page];

  useEffect(() => {
    // `replace`, so Back can't bounce anyone into the retired page.
    const timer = setTimeout(() => window.location.replace(url), AUTO_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [url]);

  return (
    <Page className={classes.root} title={copy.title}>
      <Container maxWidth="sm">
        <Card>
          <CardContent className={classes.cardContent}>
            <Logo />
            <Box mt={4}>
              <Typography variant="h3" color="textPrimary">
                {copy.heading}
              </Typography>
            </Box>
            <Box mt={2} maxWidth={420}>
              <Typography variant="body1" color="textSecondary">
                {copy.body}
              </Typography>
            </Box>
            <Box mt={4}>
              <Button
                variant="contained"
                size="large"
                className={classes.cta}
                href={url}
                endIcon={<ArrowForwardIcon />}
              >
                Continue to the new TrackTaco
              </Button>
            </Box>
            <div className={classes.progress} aria-live="polite">
              <LinearProgress />
              <Box mt={1}>
                <Typography variant="caption" color="textSecondary">
                  Taking you there now…
                </Typography>
              </Box>
            </div>
          </CardContent>
        </Card>
      </Container>
    </Page>
  );
};

export default RedirectToV2;
